import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Delete, Edit } from '@mui/icons-material'
import { Autocomplete, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { FeaturePositionOption, positionKey } from '../helpers/featureHelpers'

type CustomProps = {
  disabled: boolean
  editedKeys: string[]
  loadingKey?: string
  onRemove: (position: FeaturePositionOption) => void
  onEditExample: (position: FeaturePositionOption) => void
}

type FeaturePositionsProps = CustomProps & {
  positions: FeaturePositionOption[]
  available: FeaturePositionOption[]
  onAdd: (position: FeaturePositionOption) => void
}

export const FeaturePositions: FC<FeaturePositionsProps> = ({ positions, available, disabled, editedKeys, loadingKey, onAdd, onRemove, onEditExample }) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<FeaturePositionOption | null>(available[0] ?? null)

  useEffect(() => {
    setSelected((current) => (current && available.some((option) => positionKey(option) === positionKey(current)) ? current : available[0] ?? null))
  }, [available])

  const tableColumns: GRTableColumn<FeaturePositionOption, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:categories_position'),
        accessor: ({ row, customTableProps }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{row.name}</Typography>
            {customTableProps?.editedKeys.includes(positionKey(row)) && (
              <Chip size="small" color="warning" label={t('internal-analysts:categories_example_edited_chip')} />
            )}
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => (
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              size="small"
              startIcon={<Edit />}
              onClick={() => customTableProps?.onEditExample(row)}
              disabled={customTableProps?.disabled || !!customTableProps?.loadingKey}
              aria-label={t('internal-analysts:categories_edit_example_named', { position: row.name })}
            >
              {customTableProps?.loadingKey === positionKey(row)
                ? t('internal-analysts:categories_example_loading')
                : t('internal-analysts:categories_edit_example')}
            </Button>
            <Tooltip title={t('internal-analysts:categories_position_remove')}>
              <span>
                <IconButton
                  color="error"
                  aria-label={t('internal-analysts:categories_position_remove_named', { position: row.name })}
                  onClick={() => customTableProps?.onRemove(row)}
                  disabled={customTableProps?.disabled}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
        cellProps: { align: 'right' },
        headerCellProps: { sx: { width: 240, minWidth: 240 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)
  const customProps = useMemo<CustomProps>(
    () => ({ disabled, editedKeys, loadingKey, onRemove, onEditExample }),
    [disabled, editedKeys, loadingKey, onRemove, onEditExample]
  )

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Autocomplete
          sx={{ minWidth: 320 }}
          size="small"
          options={available}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => positionKey(option) === positionKey(value)}
          disabled={disabled || !available.length}
          renderInput={(params) => <TextField {...params} label={t('internal-analysts:categories_position_select')} />}
        />
        <Button variant="outlined" startIcon={<Add />} disabled={disabled || !selected} onClick={() => selected && onAdd(selected)}>
          {t('internal-analysts:categories_position_add')}
        </Button>
      </Stack>
      <GRTable
        rows={positions}
        columns={columns}
        onColumnsUpdated={setColumns}
        rowIdKey={(row) => positionKey(row)}
        customProps={customProps}
        noRowsLabel={t('internal-analysts:categories_no_positions')}
        striped
      />
    </Stack>
  )
}
